/**
 * MIRAE Jewelry - Dynamic Pricing Engine
 * Calculates product prices based on daily gold/silver rates
 */

export class PriceCalculator {
  constructor(metalPrices) {
    this.metalPrices = metalPrices || {
      gold: 5200, // Default: ₹5200/gm
      silver: 75, // Default: ₹75/gm
    };
  }

  /**
   * Update daily metal prices
   */
  updateMetalPrices(goldPrice, silverPrice) {
    this.metalPrices = {
      gold: goldPrice,
      silver: silverPrice
    };
  }

  /**
   * Calculate final price based on product type
   */
  calculatePrice(product) {
    switch (product.type) {
      case 'fixed':
        return this.calculateFixedPrice(product);
      case 'wastage':
        return this.calculateWastagePrice(product);
      case 'labour':
        return this.calculateLabourPrice(product);
      default:
        return product.basePrice;
    }
  }

  /**
   * Fixed Price - No metal price impact
   */
  calculateFixedPrice(product) {
    return product.basePrice;
  }

  /**
   * Wastage Calculation: (Metal Price × Weight) + Wastage % + Labour
   */
  calculateWastagePrice(product) {
    const metalRate = this.getMetalRate(product.category, product.metalPurity);
    const metalCost = metalRate * product.metalWeight;
    const wastageCost = metalCost * (product.wastagePercentage / 100);
    const totalPrice = metalCost + wastageCost + product.labourCharge;
    
    return this.roundToNearest(totalPrice, 5); // Round to nearest 5
  }

  /**
   * Labour + Metal: (Metal Price × Weight) + Fixed Labour
   */
  calculateLabourPrice(product) {
    const metalRate = this.getMetalRate(product.category, product.metalPurity);
    const metalCost = metalRate * product.metalWeight;
    const totalPrice = metalCost + product.labourCharge;
    
    return this.roundToNearest(totalPrice, 5);
  }

  /**
   * Get metal rate based on category and purity
   */
  getMetalRate(category, purity) {
    if (category === 'gold') {
      return this.calculateGoldRate(purity);
    } else if (category === 'silver') {
      return this.calculateSilverRate(purity);
    }
    return 0;
  }

  /**
   * Calculate gold rate based on purity
   */
  calculateGoldRate(purity) {
    const baseGoldRate = this.metalPrices.gold;
    
    switch (purity) {
      case '24k':
        return baseGoldRate;
      case '22k':
        return baseGoldRate * 0.916; // 91.6% purity
      case '18k':
        return baseGoldRate * 0.75; // 75% purity
      case '14k':
        return baseGoldRate * 0.585; // 58.5% purity
      default:
        return baseGoldRate;
    }
  }

  /**
   * Calculate silver rate based on purity
   */
  calculateSilverRate(purity) {
    const baseSilverRate = this.metalPrices.silver;
    
    switch (purity) {
      case '999':
        return baseSilverRate;
      case '925':
        return baseSilverRate * 0.925; // 92.5% purity
      default:
        return baseSilverRate;
    }
  }

  /**
   * Round to nearest value (e.g., nearest 5 or 10)
   */
  roundToNearest(price, roundTo = 5) {
    return Math.round(price / roundTo) * roundTo;
  }

  /**
   * Format price with Indian numbering system
   */
  formatIndianPrice(price) {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  }

  /**
   * Calculate making charges percentage
   */
  calculateMakingCharges(product) {
    const finalPrice = this.calculatePrice(product);
    const metalRate = this.getMetalRate(product.category, product.metalPurity);
    const metalCost = metalRate * product.metalWeight;
    const makingCharges = finalPrice - metalCost;
    const makingPercentage = (makingCharges / metalCost) * 100;
    
    return {
      makingCharges,
      makingPercentage: makingPercentage.toFixed(2)
    };
  }

  /**
   * Bulk price calculation for multiple products
   */
  calculateBulkPrices(products) {
    return products.map(product => ({
      ...product,
      finalPrice: this.calculatePrice(product),
      makingDetails: product.type !== 'fixed' ? 
        this.calculateMakingCharges(product) : null
    }));
  }
}

export default PriceCalculator;